(function () {
  'use strict';

  if (window.DyeforAIImageBridge) return;

  var VERSION = '1.4.13';
  var API_BASE = 'https://dyefor-ai-studio.doimage.workers.dev';
  var MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
  var DEFAULT_TIMEOUT_MS = 20000;
  var AI_UPLOAD_TIMEOUT_MS = 180000;
  var AUTO_RESUME_DELAY_MS = 350;
  var PENDING_MAX_AGE_MS = 30 * 60 * 1000;
  var STORAGE_KEY = 'dyefor.ai-customizer.pending.v1';
  var HANDOFF_PARAM = 'dyefor_ai_handoff';
  var HANDOFF_FRAGMENT_PARAM = 'design';
  var DIRECT_AI_HANDLES = [
    'custom-ai-iphone-case',
    'custom-ai-wrapping-paper',
    'custom-ai-banner'
  ];
  var DIRECT_AI_PHONE_PRINT_LAYER = Object.freeze({ width: 1133, height: 1984 });
  var retainedHandoffToken = (function () {
    var params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    var token = params.get(HANDOFF_FRAGMENT_PARAM) || '';
    return /^[A-Za-z0-9_-]{43}$/.test(token) ? token : '';
  })();
  var UPLOADABLE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/bmp'];
  var CONVERTIBLE_TYPES = ['image/webp', 'image/avif', 'image/gif'];
  var purchaseBlocked = false;
  var tokenResumePromise = null;
  var pendingAICropUploads = 0;
  var cropControlSyncTimer = null;
  var directCropPreparationPromises = Object.create(null);
  var directCustomizationData = '';
  var directApprovedPreviewUrl = '';
  var directApprovedProductPreview = null;
  var directVariantRenderTimer = null;
  var directVariantRenderSequence = 0;
  var directVariantReadyId = '';
  var directVariantPreviewCache = Object.create(null);

  function isDirectAIHandoff() {
    return DIRECT_AI_HANDLES.indexOf(productHandleFromLocation()) !== -1 && Boolean(handoffTokenFromUrl());
  }

  if (isDirectAIHandoff()) {
    purchaseBlocked = true;
    document.documentElement.classList.add(
      'dyefor-ai-direct-product',
      'dyefor-ai-handoff-loading',
      'dyefor-ai-handoff-blocked'
    );
  }

  function bridgeError(code, message, cause) {
    var error = new Error(message);
    error.name = 'DyeforAIImageBridgeError';
    error.code = code;
    if (cause) error.cause = cause;
    return error;
  }

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail }));
  }

  function trimValue(value, maxLength) {
    if (value === undefined || value === null) return '';
    return String(value).trim().slice(0, maxLength || 255);
  }

  function normaliseMimeType(value) {
    var type = trimValue(value, 100).toLowerCase().split(';')[0];
    return type === 'image/jpg' ? 'image/jpeg' : type;
  }

  function inferMimeType(filename) {
    var extension = trimValue(filename, 255).toLowerCase().split('.').pop();
    if (extension === 'png') return 'image/png';
    if (extension === 'jpg' || extension === 'jpeg') return 'image/jpeg';
    if (extension === 'bmp') return 'image/bmp';
    if (extension === 'webp') return 'image/webp';
    if (extension === 'avif') return 'image/avif';
    if (extension === 'gif') return 'image/gif';
    return '';
  }

  function safeFilename(filename, mimeType) {
    var extension = mimeType === 'image/png' ? '.png' : mimeType === 'image/bmp' ? '.bmp' : '.jpg';
    var matchingExtension = mimeType === 'image/png'
      ? /\.png$/i
      : mimeType === 'image/bmp'
        ? /\.bmp$/i
        : /\.jpe?g$/i;
    var value = trimValue(filename, 180)
      .replace(/[^a-zA-Z0-9._-]+/g, '-')
      .replace(/^-+|-+$/g, '');

    if (!value) value = 'dyefor-ai-design' + extension;
    if (!matchingExtension.test(value)) {
      value = value.replace(/\.[a-zA-Z0-9]{1,5}$/i, '') + extension;
    }
    return value;
  }

  function getDescriptor(source) {
    if (source && typeof source === 'object' && source.asset) return source.asset;
    return source;
  }

  function getMetadata(source, options) {
    var root = source && typeof source === 'object' ? source : {};
    var descriptor = getDescriptor(source);
    descriptor = descriptor && typeof descriptor === 'object' ? descriptor : {};
    options = options || {};

    return {
      designId: trimValue(options.designId || root.designId || descriptor.designId, 255),
      requestId: trimValue(options.requestId || root.requestId || descriptor.requestId, 255),
      assetId: trimValue(options.assetId || root.assetId || descriptor.assetId, 255),
      printProfile: trimValue(options.printProfile || root.printProfile || '', 80)
    };
  }

  function getTargetElements() {
    return Array.prototype.slice.call(document.querySelectorAll('.hdnFile')).map(function (input) {
      var parent = input.closest('.cst-dv-parent');
      if (!parent) return null;
      return { input: input, parent: parent };
    }).filter(Boolean);
  }

  function describeTarget(target, index) {
    var label = target.parent.querySelector('label:not(.progress)');
    return {
      id: trimValue(target.parent.getAttribute('data-cst-txt-id'), 255),
      label: label ? trimValue(label.textContent, 255) : '',
      index: index,
      complete: target.parent.classList.contains('done')
    };
  }

  function getTargets() {
    return getTargetElements().map(describeTarget);
  }

  function isAvailable() {
    return Boolean(
      getTargetElements().length ||
      document.querySelector('dyefor-customizer .btnStartCust, .btnStartCust') ||
      typeof window.StartCustomizer === 'function'
    );
  }

  function waitFor(predicate, timeoutMs) {
    return new Promise(function (resolve, reject) {
      var result = predicate();
      if (result) {
        resolve(result);
        return;
      }

      var settled = false;
      var observer = new MutationObserver(function () {
        var nextResult = predicate();
        if (!nextResult || settled) return;
        settled = true;
        window.clearTimeout(timer);
        observer.disconnect();
        resolve(nextResult);
      });

      observer.observe(document.documentElement, { childList: true, subtree: true });

      var timer = window.setTimeout(function () {
        if (settled) return;
        settled = true;
        observer.disconnect();
        reject(bridgeError('CUSTOMIZER_TIMEOUT', 'The customiser did not expose an image field in time.'));
      }, timeoutMs || DEFAULT_TIMEOUT_MS);
    });
  }

  function isCropControl(element) {
    if (!element || !element.matches || !element.matches('input[type="button"], button')) return false;
    var label = trimValue(element.value || element.textContent, 80).toLowerCase();
    return label === 'crop';
  }

  function syncAICropControls() {
    Array.prototype.forEach.call(document.querySelectorAll('input[type="button"], button'), function (control) {
      if (!isCropControl(control)) return;
      if (pendingAICropUploads > 0) {
        if (!control.disabled) control.setAttribute('data-dyefor-ai-waiting', 'true');
        control.disabled = true;
        control.setAttribute('aria-busy', 'true');
      } else if (control.getAttribute('data-dyefor-ai-waiting') === 'true') {
        control.disabled = false;
        control.removeAttribute('aria-busy');
        control.removeAttribute('data-dyefor-ai-waiting');
      }
    });
  }

  function setAICropUploadPending(pending) {
    pendingAICropUploads = Math.max(0, pendingAICropUploads + (pending ? 1 : -1));
    document.documentElement.classList.toggle('dyefor-ai-upload-pending', pendingAICropUploads > 0);
    if (pendingAICropUploads > 0 && cropControlSyncTimer === null) {
      cropControlSyncTimer = window.setInterval(syncAICropControls, 100);
    }
    if (pendingAICropUploads === 0 && cropControlSyncTimer !== null) {
      window.clearInterval(cropControlSyncTimer);
      cropControlSyncTimer = null;
    }
    syncAICropControls();
  }

  function waitForHighResolutionUpload(target, timeoutMs) {
    var targetId = trimValue(target.parent.getAttribute('data-cst-txt-id'), 255);
    if (!targetId) return Promise.reject(bridgeError('UPLOAD_TARGET_MISSING', 'The personaliser image field has no identifier.'));

    target.parent.setAttribute('data-dyefor-ai-upload-pending', 'true');
    setAICropUploadPending(true);

    return new Promise(function (resolve, reject) {
      var startedAt = Date.now();
      var limit = timeoutMs || AI_UPLOAD_TIMEOUT_MS;
      var uploadPromiseBound = false;
      var settled = false;

      function finish(callback, value) {
        if (settled) return;
        settled = true;
        target.parent.removeAttribute('data-dyefor-ai-upload-pending');
        setAICropUploadPending(false);
        callback(value);
      }

      function check() {
        if (settled) return;
        var checkbox = target.parent.querySelector('.cst_cb_uploded, .cst_cb_uploaded');
        var checked = Boolean(checkbox && checkbox.checked);
        if (checked && target.parent.classList.contains('done')) {
          finish(resolve, { state: 'complete', targetId: targetId });
          return;
        }

        var upload = window.objPromUpldFile && window.objPromUpldFile[targetId];
        var highResolution = upload && upload.hiRes;
        if (!uploadPromiseBound && highResolution && typeof highResolution.then === 'function') {
          uploadPromiseBound = true;
          Promise.resolve(highResolution).then(function (value) {
            finish(resolve, { state: 'uploaded', targetId: targetId, value: value });
          }, function (error) {
            finish(reject, bridgeError('HIGH_RES_UPLOAD_FAILED', 'The AI artwork could not be prepared for the personaliser.', error));
          });
          return;
        }

        if (Date.now() - startedAt >= limit) {
          finish(reject, bridgeError('HIGH_RES_UPLOAD_TIMEOUT', 'The AI artwork took too long to prepare. Please try again.'));
          return;
        }
        window.setTimeout(check, 100);
      }

      check();
    });
  }

  function openCustomizer(options) {
    options = options || {};
    if (getTargetElements().length) return Promise.resolve(getTargets());

    if (!document.querySelector('.varDlgCust')) {
      var button = document.querySelector('dyefor-customizer .btnStartCust, .btnStartCust');
      if (button && !button.disabled) {
        button.click();
      } else if (typeof window.StartCustomizer === 'function') {
        window.StartCustomizer();
      } else {
        return Promise.reject(bridgeError('NOT_CUSTOMISABLE', 'This page does not expose the Dyefor image customiser.'));
      }
    }

    return waitFor(function () {
      return getTargetElements().length ? getTargets() : null;
    }, options.timeoutMs);
  }

  function waitForCustomizerRuntime(timeoutMs) {
    if (typeof window.getLayerToWork !== 'function') return Promise.resolve(null);

    return new Promise(function (resolve, reject) {
      var startedAt = Date.now();
      var limit = timeoutMs || DEFAULT_TIMEOUT_MS;

      function check() {
        var layer = null;
        try {
          layer = window.getLayerToWork();
        } catch (error) {}

        if (layer && typeof layer.getChildren === 'function') {
          resolve(layer);
          return;
        }
        if (Date.now() - startedAt >= limit) {
          reject(bridgeError('CUSTOMIZER_RUNTIME_TIMEOUT', 'The personaliser was not ready to receive the AI artwork.'));
          return;
        }
        window.setTimeout(check, 50);
      }

      check();
    });
  }

  function validateAssetUrl(value, allowBlob) {
    var url;
    try {
      url = new URL(value, window.location.href);
    } catch (error) {
      throw bridgeError('INVALID_ASSET_URL', 'The AI result did not contain a valid asset URL.', error);
    }

    var sameOriginHttp = url.origin === window.location.origin && (url.protocol === 'http:' || url.protocol === 'https:');
    if (url.protocol !== 'https:' && !sameOriginHttp && !(allowBlob && url.protocol === 'blob:')) {
      throw bridgeError('INSECURE_ASSET_URL', 'AI assets must use HTTPS or the current shop origin.');
    }
    return url;
  }

  function fetchBlob(url) {
    var parsedUrl = validateAssetUrl(url, true);
    return fetch(parsedUrl.href, {
      method: 'GET',
      mode: parsedUrl.protocol === 'blob:' ? 'same-origin' : 'cors',
      credentials: parsedUrl.origin === window.location.origin ? 'same-origin' : 'omit',
      cache: 'no-store'
    }).then(function (response) {
      if (!response.ok) {
        throw bridgeError('ASSET_FETCH_FAILED', 'The AI image could not be downloaded (' + response.status + ').');
      }
      return response.blob();
    }).catch(function (error) {
      if (error && error.name === 'DyeforAIImageBridgeError') throw error;
      throw bridgeError('ASSET_FETCH_FAILED', 'The AI image could not be downloaded. Check its CORS policy and expiry.', error);
    });
  }

  function dataUrlToBlob(dataUrl) {
    if (!/^data:image\//i.test(dataUrl)) {
      return Promise.reject(bridgeError('INVALID_DATA_URL', 'Only image data URLs can be imported.'));
    }
    return fetch(dataUrl).then(function (response) {
      return response.blob();
    });
  }

  function drawBlobToPng(blob) {
    function canvasBlob(image, width, height, cleanup) {
      return new Promise(function (resolve, reject) {
        var canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        try {
          canvas.getContext('2d').drawImage(image, 0, 0);
        } catch (error) {
          cleanup();
          reject(bridgeError('IMAGE_CONVERSION_FAILED', 'The AI image could not be converted to PNG.', error));
          return;
        }

        canvas.toBlob(function (converted) {
          cleanup();
          canvas.width = 1;
          canvas.height = 1;
          if (!converted) {
            reject(bridgeError('IMAGE_CONVERSION_FAILED', 'The AI image could not be converted to PNG.'));
            return;
          }
          resolve(converted);
        }, 'image/png');
      });
    }

    if (typeof window.createImageBitmap === 'function') {
      return window.createImageBitmap(blob).then(function (bitmap) {
        return canvasBlob(bitmap, bitmap.width, bitmap.height, function () {
          if (bitmap.close) bitmap.close();
        });
      });
    }

    return new Promise(function (resolve, reject) {
      var image = new Image();
      var objectUrl = URL.createObjectURL(blob);
      image.onload = function () {
        canvasBlob(image, image.naturalWidth, image.naturalHeight, function () {
          URL.revokeObjectURL(objectUrl);
        }).then(resolve, reject);
      };
      image.onerror = function () {
        URL.revokeObjectURL(objectUrl);
        reject(bridgeError('IMAGE_CONVERSION_FAILED', 'The AI image could not be decoded.'));
      };
      image.src = objectUrl;
    });
  }

  function makeFile(blob, filename, mimeType) {
    try {
      return new File([blob], filename, { type: mimeType, lastModified: Date.now() });
    } catch (error) {
      blob.name = filename;
      blob.lastModified = Date.now();
      return blob;
    }
  }

  function normaliseSource(source, options) {
    options = options || {};
    var descriptor = getDescriptor(source);
    var filename = options.filename || (descriptor && descriptor.filename);
    var mimeType = options.mimeType || (descriptor && descriptor.mimeType);
    var blobPromise;

    if (typeof File !== 'undefined' && descriptor instanceof File) {
      filename = filename || descriptor.name;
      blobPromise = Promise.resolve(descriptor);
    } else if (typeof Blob !== 'undefined' && descriptor instanceof Blob) {
      blobPromise = Promise.resolve(descriptor);
    } else if (typeof descriptor === 'string') {
      blobPromise = descriptor.indexOf('data:image/') === 0 ? dataUrlToBlob(descriptor) : fetchBlob(descriptor);
    } else if (descriptor && typeof File !== 'undefined' && descriptor.file instanceof File) {
      filename = filename || descriptor.file.name;
      blobPromise = Promise.resolve(descriptor.file);
    } else if (descriptor && typeof Blob !== 'undefined' && descriptor.blob instanceof Blob) {
      blobPromise = Promise.resolve(descriptor.blob);
    } else if (descriptor && descriptor.dataUrl) {
      blobPromise = dataUrlToBlob(descriptor.dataUrl);
    } else if (descriptor && descriptor.url) {
      blobPromise = fetchBlob(descriptor.url);
    } else {
      return Promise.reject(bridgeError('MISSING_ASSET', 'No AI image file, blob, data URL or URL was supplied.'));
    }

    return blobPromise.then(function (blob) {
      var reportedType = normaliseMimeType(blob.type);
      var requestedType = normaliseMimeType(mimeType || inferMimeType(filename));
      var type = reportedType.indexOf('image/') === 0 ? reportedType : requestedType;
      if (!type || type.indexOf('image/') !== 0) {
        throw bridgeError('UNSUPPORTED_FILE_TYPE', 'The AI result is not a recognised image.');
      }

      if (CONVERTIBLE_TYPES.indexOf(type) !== -1) {
        return drawBlobToPng(blob).then(function (converted) {
          return {
            blob: converted,
            mimeType: 'image/png',
            filename: safeFilename(filename || 'dyefor-ai-design.png', 'image/png')
          };
        });
      }

      if (UPLOADABLE_TYPES.indexOf(type) === -1) {
        throw bridgeError('UNSUPPORTED_FILE_TYPE', 'Use a PNG, JPEG, BMP, WebP, AVIF or GIF image.');
      }

      return {
        blob: blob,
        mimeType: type,
        filename: safeFilename(filename, type)
      };
    }).then(function (asset) {
      if (asset.blob.size > MAX_UPLOAD_BYTES) {
        throw bridgeError('FILE_TOO_LARGE', 'The AI image is larger than the customiser limit of 20 MB.');
      }
      asset.file = makeFile(asset.blob, asset.filename, asset.mimeType);
      return asset;
    });
  }

  function selectTarget(options) {
    options = options || {};
    var targets = getTargetElements();
    if (!targets.length) throw bridgeError('NO_IMAGE_FIELD', 'The customiser has no image field.');

    if (options.targetId) {
      var wantedId = String(options.targetId);
      var exact = targets.find(function (target) {
        return target.parent.getAttribute('data-cst-txt-id') === wantedId;
      });
      if (!exact) throw bridgeError('TARGET_NOT_FOUND', 'The requested customiser image field was not found.');
      return exact;
    }

    if (Number.isInteger(options.targetIndex) && targets[options.targetIndex]) {
      return targets[options.targetIndex];
    }

    return targets.find(function (target) {
      return !target.parent.classList.contains('done');
    }) || targets[0];
  }

  function productForms() {
    var forms = Array.prototype.slice.call(document.querySelectorAll(
      'form[data-type="add-to-cart-form"], form[action*="/cart/add"]'
    ));
    return forms.filter(function (form, index) {
      return forms.indexOf(form) === index;
    });
  }

  function setPrivateProperty(name, value) {
    if (!value) return;
    var inputName = 'properties[' + name + ']';
    var forms = productForms();
    var containers = forms.length ? forms : [document.querySelector('dyefor-customizer')].filter(Boolean);

    containers.forEach(function (container) {
      var input = Array.prototype.slice.call(container.querySelectorAll('input[type="hidden"]')).find(function (candidate) {
        return candidate.name === inputName;
      });
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = inputName;
        input.setAttribute('data-dyefor-ai-property', 'true');
        container.appendChild(input);
      }
      input.value = trimValue(value, 255);
    });
  }

  function setMetadata(metadata) {
    setPrivateProperty('_ai_source', 'dyefor-ai');
    setPrivateProperty('_ai_design_id', metadata.designId);
    setPrivateProperty('_ai_request_id', metadata.requestId);
    setPrivateProperty('_ai_asset_id', metadata.assetId);
    if (metadata.printProfile) {
      if (metadata.printProfile !== 'wrap198-v1') throw bridgeError('INVALID_PRINT_PROFILE', 'This print profile is not supported.');
      setPrivateProperty('_ai_print_profile', metadata.printProfile);
    } else {
      document.querySelectorAll('input[name="properties[_ai_print_profile]"]').forEach(function (input) { input.remove(); });
    }
  }

  function decodeUtf16Binary(binary) {
    if (!binary || binary.length % 2 !== 0) return '';
    var value = '';
    for (var index = 0; index < binary.length; index += 2) {
      value += String.fromCharCode(binary.charCodeAt(index) | (binary.charCodeAt(index + 1) << 8));
    }
    return value;
  }

  function encodeUtf16Base64(value) {
    var binary = '';
    for (var index = 0; index < value.length; index += 1) {
      var unit = value.charCodeAt(index);
      binary += String.fromCharCode(unit & 255, unit >>> 8);
    }
    return window.btoa(binary);
  }

  function replaceCustomizationPreview(encoded, previewUrl) {
    var approvedUrl = trimValue(previewUrl, 2048);
    if (!encoded || !approvedUrl) return encoded;

    var binary;
    try {
      binary = window.atob(encoded);
    } catch (error) {
      return encoded;
    }

    var candidates = [
      { value: decodeUtf16Binary(binary), encoding: 'utf16' },
      { value: binary, encoding: 'plain' }
    ];
    for (var candidateIndex = 0; candidateIndex < candidates.length; candidateIndex += 1) {
      var candidate = candidates[candidateIndex];
      var payload;
      try {
        payload = JSON.parse(candidate.value);
      } catch (error) {
        continue;
      }
      if (!Array.isArray(payload)) continue;
      var preview = payload.find(function (item) {
        return item && item.id === 'preview';
      });
      if (!preview) continue;
      preview.val = approvedUrl;
      var serialized = JSON.stringify(payload);
      return candidate.encoding === 'utf16'
        ? encodeUtf16Base64(serialized)
        : window.btoa(serialized);
    }
    return encoded;
  }

  function syncApprovedPreview(value) {
    var current = String(value || customizationDataValue()).trim();
    if (!current || !directApprovedPreviewUrl) return current;
    return replaceCustomizationPreview(current, directApprovedPreviewUrl);
  }

  function rememberApprovedPreviewUrl(value) {
    if (!value) return;
    try {
      var url = validateAssetUrl(value, false);
      if (url.protocol === 'https:' && url.href.length <= 2048) directApprovedPreviewUrl = url.href;
    } catch (error) {}
  }

  function installApprovedPreviewValidationBridge() {
    var original = window.customizationPerformValidationOnAdd;
    if (typeof original !== 'function' || original.dyeforApprovedPreviewBridge) return;

    function validateWithApprovedPreview() {
      var context = this;
      var args = arguments;
      return Promise.resolve(original.apply(context, args)).then(function (valid) {
        if (valid !== false) {
          var updatedCustomizationData = syncApprovedPreview();
          if (updatedCustomizationData) syncCustomizationData(updatedCustomizationData);
        }
        return valid;
      });
    }
    validateWithApprovedPreview.dyeforApprovedPreviewBridge = true;
    window.customizationPerformValidationOnAdd = validateWithApprovedPreview;
  }

  function productHandleFromLocation() {
    var match = window.location.pathname.match(/^\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  function handoffTokenFromUrl() {
    var params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    var token = params.get(HANDOFF_FRAGMENT_PARAM) || '';
    if (/^[A-Za-z0-9_-]{43}$/.test(token)) retainedHandoffToken = token;
    return retainedHandoffToken;
  }

  function preserveHandoffFragment() {
    if (!retainedHandoffToken || DIRECT_AI_HANDLES.indexOf(productHandleFromLocation()) === -1) return;
    var url = new URL(window.location.href);
    var params = new URLSearchParams(url.hash.replace(/^#/, ''));
    if (params.get(HANDOFF_FRAGMENT_PARAM) === retainedHandoffToken) return;
    params.set(HANDOFF_FRAGMENT_PARAM, retainedHandoffToken);
    url.hash = params.toString();
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  }

  function setPurchaseBlocked(blocked) {
    purchaseBlocked = Boolean(blocked);
    document.documentElement.classList.toggle('dyefor-ai-handoff-blocked', purchaseBlocked);
  }

  function finishDirectProductLoading() {
    document.documentElement.classList.remove('dyefor-ai-handoff-loading');
  }

  function revealDirectProductPreviewWhenReady(image) {
    if (!isDirectAIHandoff() || !image) return;

    function reveal() {
      if (!image.naturalWidth) return;
      finishDirectProductLoading();
    }

    if (image.complete && image.naturalWidth) {
      reveal();
      return;
    }
    image.addEventListener('load', reveal, { once: true });
  }

  function showHandoffStatus(message, state) {
    var container = document.querySelector('.product__info-container, .product-form, form[action*="/cart/add"]');
    if (!container) return;
    var status = document.querySelector('[data-dyefor-ai-handoff-status]');
    if (!status) {
      status = document.createElement('div');
      status.setAttribute('data-dyefor-ai-handoff-status', 'true');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.className = 'dyefor-ai-handoff-status';
      container.insertBefore(status, container.firstChild);
    }
    status.className = 'dyefor-ai-handoff-status is-' + (state || 'loading');
    status.textContent = message;
  }

  document.addEventListener('click', function (event) {
    if (pendingAICropUploads <= 0) return;
    var control = event.target.closest && event.target.closest('input[type="button"], button');
    if (!isCropControl(control)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  document.addEventListener('click', function (event) {
    var button = event.target.closest('button[name="add"], [data-atc-button], .product-form__submit');
    if (!button) return;
    installApprovedPreviewValidationBridge();
    if (directCustomizationData) syncCustomizationData(directCustomizationData);
    if (!purchaseBlocked) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  document.addEventListener('submit', function (event) {
    if (!event.target.matches('form[action*="/cart/add"]')) return;
    if (directCustomizationData) syncCustomizationData(directCustomizationData);
    if (!purchaseBlocked) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  function assignFile(input, file) {
    input.value = '';
    try {
      var transfer = new DataTransfer();
      transfer.items.add(file);
      input.files = transfer.files;
    } catch (error) {
      try {
        Object.defineProperty(input, 'files', { configurable: true, value: [file] });
      } catch (fallbackError) {
        throw bridgeError('FILE_ASSIGNMENT_FAILED', 'This browser could not pass the AI image to the customiser.', fallbackError);
      }
    }

    if (!input.files || !input.files.length) {
      throw bridgeError('FILE_ASSIGNMENT_FAILED', 'The AI image was not attached to the customiser.');
    }

    var changeEvent;
    try {
      changeEvent = new Event('change', { bubbles: true });
    } catch (error) {
      changeEvent = document.createEvent('Event');
      changeEvent.initEvent('change', true, false);
    }
    input.dispatchEvent(changeEvent);
  }

  function importImage(source, options) {
    options = options || {};
    var metadata = getMetadata(source, options);
    var targetOptions = {
      targetId: options.targetId || (source && source.targetId),
      targetIndex: options.targetIndex,
      timeoutMs: options.timeoutMs
    };

    emit('dyefor:ai-image-import-started', { metadata: metadata });

    return normaliseSource(source, options).then(function (asset) {
      return openCustomizer(targetOptions).then(function () {
        return waitForCustomizerRuntime(targetOptions.timeoutMs).then(function () {
          var target = selectTarget(targetOptions);
          var uploadButton = target.parent.querySelector('.upload-btn-wrapper');
          if (!uploadButton || typeof window.changeSelById !== 'function') {
            throw bridgeError('CUSTOMIZER_API_MISSING', 'The active customiser does not expose its image selection hook.');
          }

          window.changeSelById(uploadButton);
          prepareDirectAIPhonePrintLayer(runtimeImageNode(target.id));
          setMetadata(metadata);
          target.parent.setAttribute('data-dyefor-ai-image', 'true');
          assignFile(target.input, asset.file);

          return waitForHighResolutionUpload(target, options.uploadTimeoutMs).then(function () {
            var result = {
              filename: asset.filename,
              mimeType: asset.mimeType,
              bytes: asset.file.size,
              target: describeTarget(target, getTargetElements().indexOf(target)),
              metadata: metadata
            };
            emit('dyefor:ai-image-imported', result);
            return result;
          });
        });
      });
    }).catch(function (error) {
      var bridgeFailure = error && error.name === 'DyeforAIImageBridgeError'
        ? error
        : bridgeError('IMPORT_FAILED', 'The AI image could not be imported.', error);
      emit('dyefor:ai-image-error', {
        code: bridgeFailure.code,
        message: bridgeFailure.message,
        metadata: metadata
      });
      bridgeFailure.bridgeEventEmitted = true;
      throw bridgeFailure;
    });
  }

  function stageHandoff(handoffUrl, options) {
    options = options || {};
    var destination = new URL(handoffUrl, window.location.href);
    if (destination.origin !== window.location.origin) {
      throw bridgeError('INVALID_PRODUCT_URL', 'The AI handoff product must be on this Shopify store.');
    }
    var token = new URLSearchParams(destination.hash.replace(/^#/, '')).get(HANDOFF_FRAGMENT_PARAM) || '';
    if (!/^[A-Za-z0-9_-]{43}$/.test(token)) {
      throw bridgeError('INVALID_HANDOFF', 'The AI design link was not created correctly.');
    }
    emit('dyefor:ai-image-handoff-staged', {
      productUrl: destination.href,
      metadata: getMetadata(options, options)
    });
    if (options.navigate !== false) window.location.assign(destination.href);
    return destination.href;
  }

  function stageForProduct(source, productUrl, options) {
    options = options || {};
    var descriptor = getDescriptor(source);
    var sourceUrl = typeof descriptor === 'string' ? descriptor : descriptor && descriptor.url;
    if (!sourceUrl) {
      throw bridgeError('PERSISTENT_URL_REQUIRED', 'Cross-page AI handoff requires a persistent image URL.');
    }

    var assetUrl = validateAssetUrl(sourceUrl, false);
    var destination = new URL(productUrl, window.location.href);
    if (destination.origin !== window.location.origin) {
      throw bridgeError('INVALID_PRODUCT_URL', 'The AI handoff product must be on this Shopify store.');
    }

    var metadata = getMetadata(source, options);
    var preview = options.preview && typeof options.preview === 'object' ? options.preview : null;
    var previewUrl = preview && preview.url ? validateAssetUrl(preview.url, false).href : '';
    var pending = {
      version: 1,
      createdAt: Date.now(),
      expiresAt: Date.now() + PENDING_MAX_AGE_MS,
      productPathname: destination.pathname,
      url: assetUrl.href,
      filename: trimValue(options.filename || (descriptor && descriptor.filename), 180),
      mimeType: normaliseMimeType(options.mimeType || (descriptor && descriptor.mimeType)),
      targetId: trimValue(options.targetId || (source && source.targetId), 255),
      targetIndex: Number.isInteger(options.targetIndex) ? options.targetIndex : null,
      designId: metadata.designId,
      requestId: metadata.requestId,
      assetId: metadata.assetId,
      printProfile: metadata.printProfile,
      productType: trimValue(options.productType, 80),
      previewUrl: previewUrl,
      previewMimeType: normaliseMimeType(preview && preview.mimeType),
      previewLabel: trimValue(preview && preview.label, 180),
      previewWidth: Number.isFinite(Number(preview && preview.width)) ? Number(preview.width) : null,
      previewHeight: Number.isFinite(Number(preview && preview.height)) ? Number(preview.height) : null
    };

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
    } catch (error) {
      throw bridgeError('HANDOFF_STORAGE_FAILED', 'The browser could not store the AI handoff.', error);
    }

    destination.searchParams.set(HANDOFF_PARAM, '1');
    emit('dyefor:ai-image-handoff-staged', {
      productUrl: destination.href,
      metadata: metadata
    });

    if (options.navigate !== false) window.location.assign(destination.href);
    return destination.href;
  }

  function readPending() {
    var raw;
    try {
      raw = sessionStorage.getItem(STORAGE_KEY);
    } catch (error) {
      throw bridgeError('HANDOFF_STORAGE_FAILED', 'The browser could not read the AI handoff.', error);
    }
    if (!raw) return null;

    var pending;
    try {
      pending = JSON.parse(raw);
    } catch (error) {
      sessionStorage.removeItem(STORAGE_KEY);
      throw bridgeError('INVALID_HANDOFF', 'The stored AI handoff is invalid.', error);
    }

    if (pending.version !== 1 || !pending.url || pending.expiresAt < Date.now()) {
      sessionStorage.removeItem(STORAGE_KEY);
      throw bridgeError('HANDOFF_EXPIRED', 'The AI image handoff has expired.');
    }
    return pending;
  }

  function clearPending() {
    sessionStorage.removeItem(STORAGE_KEY);
  }

  function cleanHandoffUrl() {
    var url = new URL(window.location.href);
    if (!url.searchParams.has(HANDOFF_PARAM)) return;
    url.searchParams.delete(HANDOFF_PARAM);
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  }

  function resolveTokenHandoff(token, options) {
    options = options || {};
    var startedAt = Number(options.startedAt) || Date.now();
    var timeoutMs = Number(options.timeoutMs) || 90000;
    var productHandle = productHandleFromLocation();
    if (!productHandle) {
      return Promise.reject(bridgeError('WRONG_PRODUCT', 'This design link must be opened on its product page.'));
    }
    return fetch(API_BASE + '/v1/handoffs/resolve', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ token: token, productHandle: productHandle }),
      cache: 'no-store',
      credentials: 'omit',
      referrerPolicy: 'no-referrer'
    }).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (data) {
        if (response.status === 409 && data.code === 'design_not_ready' && Date.now() - startedAt < timeoutMs) {
          return new Promise(function (resolve) {
            window.setTimeout(resolve, 1000);
          }).then(function () {
            return resolveTokenHandoff(token, { startedAt: startedAt, timeoutMs: timeoutMs }).then(function (pending) {
              return { __dyeforResolvedPending: pending };
            });
          });
        }
        if (!response.ok) {
          throw bridgeError(
            data.code === 'handoff_expired' ? 'HANDOFF_EXPIRED' : 'HANDOFF_RESOLVE_FAILED',
            data.error || 'That AI design link could not be opened.'
          );
        }
        return data;
      });
    }).then(function (data) {
      if (data && data.__dyeforResolvedPending) return data.__dyeforResolvedPending;
      if (!data.asset || !data.asset.url || !data.designId) {
        throw bridgeError('INVALID_HANDOFF', 'The AI design link returned incomplete artwork.');
      }
      var productUrl = new URL(data.productUrl, window.location.href);
      if (productUrl.pathname !== window.location.pathname) {
        throw bridgeError('WRONG_PRODUCT', 'This AI design belongs to a different product.');
      }
      var suppliedPreviews = Array.isArray(data.previews) && data.previews.length
        ? data.previews
        : data.preview && typeof data.preview === 'object' ? [data.preview] : [];
      var previews = suppliedPreviews.slice(0, 3).map(function (preview) {
        return {
          url: preview && preview.url ? validateAssetUrl(preview.url, false).href : '',
          mimeType: normaliseMimeType(preview && preview.mimeType),
          label: trimValue(preview && preview.label, 180),
          width: Number.isFinite(Number(preview && preview.width)) ? Number(preview.width) : null,
          height: Number.isFinite(Number(preview && preview.height)) ? Number(preview.height) : null
        };
      }).filter(function (preview) { return Boolean(preview.url); });
      var preview = previews[0] || null;
      return {
        version: 2,
        productPathname: productUrl.pathname,
        url: validateAssetUrl(data.asset.url, false).href,
        filename: trimValue(data.asset.filename, 180),
        mimeType: normaliseMimeType(data.asset.mimeType),
        designId: trimValue(data.designId, 255),
        requestId: trimValue(data.requestId, 255),
        assetId: trimValue(data.asset.id, 255),
        printProfile: trimValue(data.printProfile && data.printProfile.id, 80),
        productType: trimValue(data.productType, 80),
        previews: previews,
        previewUrl: preview ? preview.url : '',
        previewMimeType: normaliseMimeType(preview && preview.mimeType),
        previewLabel: trimValue(preview && preview.label, 180),
        previewWidth: Number.isFinite(Number(preview && preview.width)) ? Number(preview.width) : null,
        previewHeight: Number.isFinite(Number(preview && preview.height)) ? Number(preview.height) : null
      };
    }).catch(function (error) {
      if (error && error.name === 'DyeforAIImageBridgeError') throw error;
      throw bridgeError('HANDOFF_RESOLVE_FAILED', 'That AI design link could not be opened.', error);
    });
  }

  function showProductPreview(pending) {
    if (!pending || !pending.previewUrl) return false;

    var previews = Array.isArray(pending.previews) && pending.previews.length
      ? pending.previews
      : [{
          url: pending.previewUrl,
          mimeType: pending.previewMimeType,
          label: pending.previewLabel,
          width: pending.previewWidth,
          height: pending.previewHeight
        }];
    try {
      previews = previews.map(function (preview) {
        return Object.assign({}, preview, { url: validateAssetUrl(preview.url, false).href });
      });
    } catch (error) {
      return false;
    }

    if (isDirectAIHandoff() && productHandleFromLocation() !== 'custom-ai-iphone-case') {
      directApprovedProductPreview = Object.assign({}, pending, { previews: previews });
    }

    var gallery = document.querySelector('media-gallery-colors, media-gallery');
    if (!gallery) return false;
    var items = Array.prototype.slice.call(gallery.querySelectorAll('.product__media-item'));
    var active = items.find(function (candidate) {
      return candidate.classList.contains('is-active');
    });
    var visibleItems = items.filter(function (candidate) {
      return window.getComputedStyle(candidate).display !== 'none';
    });
    var orderedItems = [];
    if (active) orderedItems.push(active);
    visibleItems.concat(items).forEach(function (item) {
      if (orderedItems.indexOf(item) === -1) orderedItems.push(item);
    });
    if (!orderedItems.length) return false;

    var thumbnails = Array.prototype.slice.call(gallery.querySelectorAll(
      '.thumbnail-list__item .thumbnail, .thumbnail-list__item button, button.thumbnail'
    ));
    var modalItems = Array.prototype.slice.call(document.querySelectorAll(
      'product-modal .product-media-modal__content [data-media-id], product-modal [data-media-id]'
    )).filter(function (item, index, all) {
      return all.indexOf(item) === index;
    });
    previews.slice(0, orderedItems.length).forEach(function (preview, index) {
      var item = orderedItems[index];
      var image = item.querySelector('modal-opener .product__media img, .product__media img, img');
      if (!image) return;
      setRenderedImageSource(
        image,
        preview.url,
        preview.label || 'Your personalised product preview',
        index,
        'data-dyefor-ai-product-preview'
      );
      if (index === 0) revealDirectProductPreviewWhenReady(image);
      if (preview.width) image.width = Number(preview.width);
      if (preview.height) image.height = Number(preview.height);

      var galleryMediaNode = item.querySelector('[data-media-id]');
      var galleryMediaId = trimValue(
        item.getAttribute('data-media-id')
          || (galleryMediaNode && galleryMediaNode.getAttribute('data-media-id')),
        255
      );
      var numericMediaId = (galleryMediaId.match(/(\d+)$/) || [])[1] || galleryMediaId;
      var modalItem = modalItems.find(function (candidate) {
        var candidateId = trimValue(candidate.getAttribute('data-media-id'), 255);
        return candidateId === galleryMediaId || candidateId === numericMediaId;
      }) || modalItems[index];
      var modalImages = modalItem && modalItem.matches('img')
        ? [modalItem]
        : modalItem ? Array.prototype.slice.call(modalItem.querySelectorAll('img')) : [];
      modalImages.forEach(function (modalImage) {
        setRenderedImageSource(
          modalImage,
          preview.url,
          preview.label || image.alt,
          index,
          'data-dyefor-ai-product-preview'
        );
        if (preview.width) modalImage.width = Number(preview.width);
        if (preview.height) modalImage.height = Number(preview.height);
      });

      var media = image.closest('.product__media');
      if (media && preview.width && preview.height) {
        media.style.paddingTop = ((Number(preview.height) / Number(preview.width)) * 100) + '%';
      }
      item.classList.add('dyefor-ai-product-preview');
      item.classList.remove('loading-thumbnail');
      item.style.removeProperty('display');

      var thumbnail = thumbnails[index] && thumbnails[index].querySelector('img');
      if (thumbnail) {
        thumbnail.removeAttribute('srcset');
        thumbnail.src = preview.url;
        thumbnail.alt = preview.label || image.alt;
      }
    });
    if (directApprovedProductPreview && isDirectAIHandoff()
      && productHandleFromLocation() !== 'custom-ai-iphone-case') {
      modalItems.forEach(function (item) {
        var hasPreview = item.hasAttribute('data-dyefor-ai-product-preview')
          || item.querySelector('[data-dyefor-ai-product-preview]');
        if (hasPreview) item.style.removeProperty('display');
        else item.style.setProperty('display', 'none');
      });
    }
    emit('dyefor:ai-product-preview-ready', {
      preview: previews[0],
      previews: previews,
      metadata: getMetadata(pending, pending)
    });
    return true;
  }

  function preserveApprovedProductPreview() {
    if (!directApprovedProductPreview || !isDirectAIHandoff()
      || productHandleFromLocation() === 'custom-ai-iphone-case') return false;

    // Gift sizes change the order, not the approved artwork scale or ribbon colour.
    showProductPreview(directApprovedProductPreview);
    return true;
  }

  function normalisePreviewAlt(value) {
    return trimValue(value, 255).replace(/\s+/g, ' ').toLowerCase();
  }

  function selectedVariantId() {
    var input = document.querySelector('[id^="product-form-installment"] [name="id"]')
      || document.querySelector('form[action*="/cart/add"] [name="id"]');
    return trimValue(input && input.value, 80);
  }

  function directVariantConfiguration(expectedVariantId) {
    var customizer = document.querySelector('dyefor-customizer');
    if (!customizer
      || typeof customizer.findSelectedVariant !== 'function'
      || typeof customizer.getSelectedVariantImages !== 'function') {
      return null;
    }

    var variant;
    var selected;
    try {
      variant = customizer.findSelectedVariant();
      selected = customizer.getSelectedVariantImages();
    } catch (error) {
      return null;
    }
    if (!variant || trimValue(variant.id, 80) !== trimValue(expectedVariantId, 80)) return null;

    var seen = Object.create(null);
    var pictures = ((selected && selected.cst && selected.cst.Pictures) || []).filter(function (picture) {
      var image = trimValue(picture && picture.image, 255);
      var alt = normalisePreviewAlt(picture && picture.imgalt);
      var key = image + '|' + alt;
      if (!image || !alt || seen[key]) return false;
      seen[key] = true;
      return true;
    }).slice(0, 3);
    if (!pictures.length) return null;

    return {
      customizer: customizer,
      variant: variant,
      pictures: pictures,
      key: trimValue(variant.id, 80) + '|' + pictures.map(function (picture) {
        return trimValue(picture.image, 255);
      }).join('|')
    };
  }

  function matchingPreviewElements(imgalt) {
    var wanted = normalisePreviewAlt(imgalt);
    return Array.prototype.filter.call(document.querySelectorAll('[data-sel-alt]'), function (element) {
      return normalisePreviewAlt(element.getAttribute('data-sel-alt')) === wanted;
    });
  }

  function setRenderedImageSource(image, url, label, index, markerName) {
    if (!image) return;
    image.removeAttribute('srcset');
    var picture = image.closest && image.closest('picture');
    Array.prototype.forEach.call(picture ? picture.querySelectorAll('source') : [], function (source) {
      source.removeAttribute('srcset');
    });
    image.src = url;
    image.alt = label || image.alt || 'Your personalised product preview';
    image.loading = 'eager';
    image.decoding = 'async';
    image.setAttribute(markerName || 'data-dyefor-ai-variant-preview', String(index + 1));
  }

  function renderedPreviewUrl(result) {
    var value = String((result && result.url) || '').trim();
    if (!value || value.length > 10 * 1024 * 1024) {
      throw bridgeError('INVALID_VARIANT_PREVIEW', 'The selected device preview was not a valid image.');
    }
    if (/^data:image\/(?:png|jpe?g|webp);base64,[a-z0-9+/=]+$/i.test(value)) return value;
    return validateAssetUrl(value, false).href;
  }

  function applyRenderedVariantPreview(preview, index) {
    var elements = matchingPreviewElements(preview.picture.imgalt);
    var images = [];
    var items = [];
    elements.forEach(function (element) {
      var item = element.closest && element.closest('.product__media-item');
      if (item && items.indexOf(item) === -1) items.push(item);
      if (element.matches && element.matches('img') && images.indexOf(element) === -1) images.push(element);
      Array.prototype.forEach.call(element.querySelectorAll ? element.querySelectorAll('img') : [], function (image) {
        if (images.indexOf(image) === -1) images.push(image);
      });
    });
    images.forEach(function (image) {
      setRenderedImageSource(image, preview.url, preview.picture.imgalt, index);
    });
    items.forEach(function (item) {
      item.classList.remove('loading-thumbnail');
      item.classList.add('dyefor-ai-product-preview');
      item.style.removeProperty('display');
    });
    elements.forEach(function (element) { element.classList.remove('loading-thumbnail'); });
    return { updated: images.length, item: items[0] || null, elements: elements };
  }

  function markDirectVariantPreviewLoading(pictures) {
    pictures.forEach(function (picture) {
      matchingPreviewElements(picture.imgalt).forEach(function (element) {
        element.classList.add('loading-thumbnail');
        var item = element.closest && element.closest('.product__media-item');
        if (item) item.classList.add('loading-thumbnail');
      });
    });
  }

  function activateRenderedVariantPreview(applied) {
    if (!applied || !applied.item) return;
    var gallery = applied.item.closest('media-gallery-colors, media-gallery');
    if (gallery && typeof gallery.setActiveMedia === 'function' && applied.item.dataset.mediaId) {
      gallery.setActiveMedia(applied.item.dataset.mediaId, true);
      return;
    }
    var button = applied.elements.map(function (element) {
      if (element.matches && element.matches('button')) return element;
      return element.querySelector && element.querySelector('button');
    }).find(Boolean);
    if (button) button.click();
  }

  function renderDirectAIVariantPreview(variant, sequence) {
    var expectedId = trimValue(variant && variant.id, 80);
    var displayName = trimValue(variant && variant.title, 120).split('/')[0].trim() || 'selected device';
    return waitForProcessState(function () {
      return directVariantConfiguration(expectedId);
    }, 5000, 'VARIANT_CONFIGURATION_TIMEOUT', 'The selected device preview could not be prepared.').then(function (configuration) {
      if (sequence !== directVariantRenderSequence) return null;
      markDirectVariantPreviewLoading(configuration.pictures);

      var cached = directVariantPreviewCache[configuration.key];
      if (cached) return { configuration: configuration, previews: cached };
      if (typeof window.cst_previewHandler !== 'function') {
        throw bridgeError('VARIANT_PREVIEW_UNAVAILABLE', 'The selected device preview service is not ready.');
      }

      return Promise.all(configuration.pictures.map(function (picture) {
        return Promise.resolve(window.cst_previewHandler(picture.image)).then(function (result) {
          return { picture: picture, url: renderedPreviewUrl(result) };
        });
      })).then(function (previews) {
        directVariantPreviewCache[configuration.key] = previews;
        return { configuration: configuration, previews: previews };
      });
    }).then(function (payload) {
      if (!payload || sequence !== directVariantRenderSequence) return null;

      Array.prototype.forEach.call(document.querySelectorAll('[data-dyefor-ai-product-preview]'), function (image) {
        image.removeAttribute('data-dyefor-ai-product-preview');
      });
      Array.prototype.forEach.call(document.querySelectorAll('[data-dyefor-ai-variant-preview]'), function (image) {
        image.removeAttribute('data-dyefor-ai-variant-preview');
      });

      var firstApplied = null;
      var updated = 0;
      payload.previews.forEach(function (preview, index) {
        var applied = applyRenderedVariantPreview(preview, index);
        if (!firstApplied && applied.updated) firstApplied = applied;
        updated += applied.updated;
      });
      if (!updated) {
        throw bridgeError('VARIANT_PREVIEW_TARGET_MISSING', 'The selected device preview could not be shown.');
      }

      activateRenderedVariantPreview(firstApplied);
      rememberApprovedPreviewUrl(payload.previews[0] && payload.previews[0].url);
      var updatedCustomizationData = syncApprovedPreview();
      if (updatedCustomizationData) syncCustomizationData(updatedCustomizationData);
      directVariantReadyId = expectedId;
      setPurchaseBlocked(false);
      showHandoffStatus('Your ' + displayName + ' preview is ready to add to basket.', 'ready');
      emit('dyefor:ai-variant-preview-ready', {
        variantId: expectedId,
        previewCount: payload.previews.length
      });
      return payload;
    }).catch(function (error) {
      if (sequence !== directVariantRenderSequence) return null;
      setPurchaseBlocked(true);
      showHandoffStatus(
        'We could not update the preview for ' + displayName + '. Please choose the device again.',
        'error'
      );
      emit('dyefor:ai-image-error', {
        code: error.code || 'VARIANT_PREVIEW_FAILED',
        message: error.message
      });
      return null;
    });
  }

  function scheduleDirectAIVariantPreview(event) {
    if (productHandleFromLocation() !== 'custom-ai-iphone-case'
      || !directCustomizationData
      || !document.documentElement.classList.contains('dyefor-ai-direct-ready')) {
      return;
    }

    var variant = event && event.detail;
    var variantId = trimValue(variant && variant.id, 80);
    if (!variantId || variantId === directVariantReadyId) return;

    window.clearTimeout(directVariantRenderTimer);
    directVariantRenderSequence += 1;
    var sequence = directVariantRenderSequence;
    var displayName = trimValue(variant.title, 120).split('/')[0].trim() || 'selected device';
    setPurchaseBlocked(true);
    showHandoffStatus('Updating your personalised preview for ' + displayName + '...', 'loading');
    directVariantRenderTimer = window.setTimeout(function () {
      renderDirectAIVariantPreview(variant, sequence);
    }, 100);
  }

  document.addEventListener('variantchanged', preserveHandoffFragment);
  document.addEventListener('variantchanged', preserveApprovedProductPreview);
  document.addEventListener('variantchanged', scheduleDirectAIVariantPreview);

  function waitForProcessState(predicate, timeoutMs, code, message) {
    return new Promise(function (resolve, reject) {
      var startedAt = Date.now();
      function check() {
        var result = null;
        try {
          result = predicate();
        } catch (error) {}
        if (result) {
          resolve(result);
          return;
        }
        if (Date.now() - startedAt >= timeoutMs) {
          reject(bridgeError(code, message));
          return;
        }
        window.setTimeout(check, 100);
      }
      check();
    });
  }

  function customizerControls() {
    var controls = [];
    Array.prototype.forEach.call(document.querySelectorAll('.varDlgCust, .cust-form-v3'), function (root) {
      Array.prototype.forEach.call(root.querySelectorAll('input[type="button"], input[type="submit"], button'), function (control) {
        if (controls.indexOf(control) === -1) controls.push(control);
      });
    });
    return controls;
  }

  function controlLabel(control) {
    return trimValue(control && (control.value || control.textContent), 80).replace(/\s+/g, ' ').toLowerCase();
  }

  function findCustomizerControl(label) {
    var wanted = String(label || '').toLowerCase();
    return customizerControls().find(function (control) {
      if (control.disabled || controlLabel(control) !== wanted) return false;
      var style = window.getComputedStyle(control);
      return style.display !== 'none' && style.visibility !== 'hidden' && control.getClientRects().length > 0;
    }) || null;
  }

  function findTargetById(targetId) {
    return Array.prototype.find.call(document.querySelectorAll('.cst-dv-parent'), function (target) {
      return target.getAttribute('data-cst-txt-id') === targetId;
    }) || null;
  }

  function targetHasProductionCrop(targetId) {
    var target = findTargetById(targetId);
    if (!target) return false;
    var checkbox = target.querySelector('.cst_cb_uploded, .cst_cb_uploaded');
    if (!target.classList.contains('done') || !checkbox || !checkbox.checked) return false;
    if (typeof window.getRuntimeLayerUniqueListOfChildrens !== 'function') return true;
    try {
      return window.getRuntimeLayerUniqueListOfChildrens().some(function (node) {
        return node && node.attrs && node.attrs.id === targetId && Boolean(node.attrs.cropData);
      });
    } catch (error) {
      return false;
    }
  }

  function runtimeImageNode(targetId) {
    if (typeof window.getRuntimeLayerUniqueListOfChildrens !== 'function') return null;
    try {
      return window.getRuntimeLayerUniqueListOfChildrens().find(function (node) {
        return node
          && node.attrs
          && node.attrs.id === targetId
          && typeof node.getClassName === 'function'
          && node.getClassName() === 'Image';
      }) || null;
    } catch (error) {
      return null;
    }
  }

  function prepareDirectAIPhonePrintLayer(imageNode) {
    if (productHandleFromLocation() !== 'custom-ai-iphone-case' || !imageNode || !imageNode.attrs) {
      return false;
    }

    var width = DIRECT_AI_PHONE_PRINT_LAYER.width;
    var height = DIRECT_AI_PHONE_PRINT_LAYER.height;
    imageNode.attrs.origSize = {
      width: width,
      height: height,
      aspectRatio: width / height
    };

    if (typeof imageNode.width === 'function') imageNode.width(width);
    else imageNode.attrs.width = width;
    if (typeof imageNode.height === 'function') imageNode.height(height);
    else imageNode.attrs.height = height;

    var y = Number(imageNode.attrs.y);
    if (!isFinite(y)) {
      y = typeof imageNode.y === 'function' ? Number(imageNode.y()) : 0;
      imageNode.attrs.y = isFinite(y) ? y : 0;
    }
    return true;
  }

  function commitDirectAIArtwork(targetId) {
    var target = findTargetById(targetId);
    var imageNode = runtimeImageNode(targetId);
    var upload = window.objPromUpldFile && window.objPromUpldFile[targetId];
    var source = upload && typeof upload.temp === 'string' ? upload.temp : '';
    if (
      !target
      || !imageNode
      || !upload
      || !/^data:image\//i.test(source)
      || typeof window.ExecuteCrop !== 'function'
    ) {
      return false;
    }

    if (directCropPreparationPromises[targetId]) return directCropPreparationPromises[targetId];
    if (!window.cropper || typeof window.cropper.getData !== 'function') return null;

    prepareDirectAIPhonePrintLayer(imageNode);
    window.selNodeObj = imageNode;
    try {
      directCropPreparationPromises[targetId] = Promise.resolve(window.ExecuteCrop()).then(function () {
        var fittedSource = upload && typeof upload.lastCrop === 'string' ? upload.lastCrop : '';
        if (!/^data:image\//i.test(fittedSource) || typeof window.chageSelGroupSrc !== 'function') {
          throw bridgeError('DIRECT_CROP_COMMIT_FAILED', 'The fitted artwork could not be prepared for checkout.');
        }
        return Promise.resolve(window.chageSelGroupSrc(fittedSource, imageNode));
      }).then(function () {
        if (typeof window.startUploadImages === 'function') {
          return Promise.resolve(window.startUploadImages());
        }
        return null;
      }).then(function () {
        return true;
      }, function (error) {
        delete directCropPreparationPromises[targetId];
        throw error;
      });
      return directCropPreparationPromises[targetId];
    } catch (error) {
      delete directCropPreparationPromises[targetId];
      return Promise.reject(error);
    }
  }

  function customizationDataValue() {
    var field = Array.prototype.find.call(document.querySelectorAll('input, textarea'), function (candidate) {
      return candidate.name === 'properties[customization_data]' && String(candidate.value || '').trim();
    });
    return field ? String(field.value || '').trim() : '';
  }

  function syncCustomizationData(value) {
    var data = String(value || '').trim();
    if (!data) return;
    directCustomizationData = data;
    productForms().forEach(function (form) {
      var fields = Array.prototype.filter.call(form.querySelectorAll('input, textarea'), function (candidate) {
        return candidate.name === 'properties[customization_data]';
      });
      if (!fields.length) {
        var field = document.createElement('input');
        field.type = 'hidden';
        field.name = 'properties[customization_data]';
        form.appendChild(field);
        fields.push(field);
      }
      fields.forEach(function (field) {
        field.value = data;
      });
    });
  }

  function finishDirectAIProduct(importResult) {
    var targetId = importResult && importResult.target && importResult.target.id;
    var cropPreparationPromise = null;
    if (!targetId) {
      return Promise.reject(bridgeError('CUSTOMIZER_TARGET_MISSING', 'The AI artwork could not be prepared for checkout.'));
    }

    return waitForProcessState(function () {
      if (targetHasProductionCrop(targetId)) return { complete: true };
      cropPreparationPromise = commitDirectAIArtwork(targetId);
      return cropPreparationPromise ? { committed: true } : null;
    }, 30000, 'DIRECT_CROP_TIMEOUT', 'The design could not be prepared for checkout.').catch(function () {
      var crop = findCustomizerControl('crop');
      if (!crop) {
        throw bridgeError('CROP_CONTROL_TIMEOUT', 'The design could not be prepared for checkout.');
      }
      crop.click();
      return { fallback: true };
    }).then(function (cropResult) {
      if (cropResult && cropResult.committed && cropPreparationPromise) return cropPreparationPromise;
      return null;
    }).catch(function () {
      var crop = findCustomizerControl('crop');
      if (!crop) {
        throw bridgeError('CROP_CONTROL_TIMEOUT', 'The design could not be prepared for checkout.');
      }
      crop.click();
      return null;
    }).then(function () {
      return waitForProcessState(function () {
        return targetHasProductionCrop(targetId);
      }, 150000, 'CROP_TIMEOUT', 'The artwork took too long to prepare for checkout.');
    }).then(function () {
      return waitForProcessState(function () {
        return findCustomizerControl('preview');
      }, 30000, 'PREVIEW_CONTROL_TIMEOUT', 'The product preview did not become ready.');
    }).then(function (previewControl) {
      previewControl.click();
      return waitForProcessState(function () {
        return findCustomizerControl('done');
      }, 150000, 'PREVIEW_TIMEOUT', 'The product details took too long to prepare.');
    }).then(function (doneControl) {
      doneControl.click();
      if (customizationDataValue()) return customizationDataValue();
      return waitForProcessState(function () {
        return typeof window.customizationPerformValidationOnAdd === 'function'
          ? true
          : null;
      }, 30000, 'CUSTOMIZATION_API_TIMEOUT', 'The order details could not be prepared.').then(function () {
        return Promise.resolve(window.customizationPerformValidationOnAdd()).then(function (valid) {
          if (!valid) {
            throw bridgeError('CUSTOMIZATION_DATA_FAILED', 'The order details could not be prepared.');
          }
          return waitForProcessState(function () {
            return customizationDataValue();
          }, 120000, 'CUSTOMIZATION_DATA_TIMEOUT', 'The order details could not be prepared.');
        });
      });
    }).then(function () {
      return waitForProcessState(function () {
        return customizationDataValue();
      }, 1000, 'CUSTOMIZATION_DATA_TIMEOUT', 'The order details could not be prepared.');
    }).then(function (customizationData) {
      syncCustomizationData(syncApprovedPreview(customizationData));
      installApprovedPreviewValidationBridge();
      directVariantReadyId = selectedVariantId();
      document.documentElement.classList.add('dyefor-ai-direct-ready');
      return importResult;
    });
  }

  function resumePending(options) {
    options = options || {};
    var currentUrl = new URL(window.location.href);
    var handoffToken = handoffTokenFromUrl();
    if (handoffToken) {
      if (tokenResumePromise) return tokenResumePromise;
      setPurchaseBlocked(true);
      showHandoffStatus('Loading your finished design...', 'loading');
      tokenResumePromise = resolveTokenHandoff(handoffToken).then(function (pending) {
        rememberApprovedPreviewUrl(pending.previewUrl);
        showProductPreview(pending);
        window.setTimeout(function () { showProductPreview(pending); }, 250);
        window.setTimeout(function () { showProductPreview(pending); }, 1000);
        return importImage(pending, pending).then(function (result) {
          return finishDirectAIProduct(result).then(function (finishedResult) {
            if (!showProductPreview(pending)) finishDirectProductLoading();
            setPurchaseBlocked(false);
            showHandoffStatus('Your finished design is ready to add to basket.', 'ready');
            emit('dyefor:ai-image-handoff-consumed', finishedResult);
            return finishedResult;
          });
        });
      }).catch(function (error) {
        finishDirectProductLoading();
        setPurchaseBlocked(true);
        showHandoffStatus(error.message || 'That AI design could not be opened.', 'error');
        if (!error.bridgeEventEmitted) {
          emit('dyefor:ai-image-error', { code: error.code || 'HANDOFF_FAILED', message: error.message });
        }
        throw error;
      });
      return tokenResumePromise;
    }
    if (!options.force && currentUrl.searchParams.get(HANDOFF_PARAM) !== '1') return Promise.resolve(null);

    var pending;
    try {
      pending = readPending();
      if (!pending) throw bridgeError('MISSING_HANDOFF', 'No pending AI image was found.');
      if (pending.productPathname !== window.location.pathname) {
        throw bridgeError('WRONG_PRODUCT', 'This AI image was staged for a different product.');
      }
      rememberApprovedPreviewUrl(pending.previewUrl);
    } catch (error) {
      emit('dyefor:ai-image-error', { code: error.code, message: error.message });
      return Promise.reject(error);
    }

    showProductPreview(pending);
    window.setTimeout(function () {
      showProductPreview(pending);
    }, 250);

    return importImage(pending, pending).then(function (result) {
      showProductPreview(pending);
      clearPending();
      cleanHandoffUrl();
      emit('dyefor:ai-image-handoff-consumed', result);
      return result;
    });
  }

  function handleReadyEvent(event) {
    var detail = event.detail || {};
    var source = detail.asset || detail.image || detail;
    var options = Object.assign({}, detail.options || {}, {
      designId: detail.designId,
      requestId: detail.requestId,
      assetId: detail.assetId,
      targetId: detail.targetId || (detail.options && detail.options.targetId),
      targetIndex: detail.targetIndex !== undefined ? detail.targetIndex : detail.options && detail.options.targetIndex
    });

    try {
      if (detail.productUrl && !isAvailable()) {
        stageForProduct(source, detail.productUrl, options);
        return;
      }
      importImage(source, options).catch(function () {});
    } catch (error) {
      emit('dyefor:ai-image-error', { code: error.code || 'HANDOFF_FAILED', message: error.message });
    }
  }

  window.DyeforAIImageBridge = Object.freeze({
    version: VERSION,
    isAvailable: isAvailable,
    getTargets: getTargets,
    openCustomizer: openCustomizer,
    importImage: importImage,
    stageHandoff: stageHandoff,
    stageForProduct: stageForProduct,
    showProductPreview: showProductPreview,
    preserveApprovedProductPreview: preserveApprovedProductPreview,
    resumePending: resumePending,
    clearPending: clearPending,
    limits: Object.freeze({
      maxUploadBytes: MAX_UPLOAD_BYTES,
      pendingMaxAgeMs: PENDING_MAX_AGE_MS,
      uploadTimeoutMs: AI_UPLOAD_TIMEOUT_MS
    })
  });

  document.addEventListener('dyefor:ai-image-ready', handleReadyEvent);

  function autoResume() {
    if (handoffTokenFromUrl()) {
      preserveHandoffFragment();
      window.setTimeout(function () {
        resumePending().catch(function () {});
      }, AUTO_RESUME_DELAY_MS);
      return;
    }
    if (new URL(window.location.href).searchParams.get(HANDOFF_PARAM) !== '1') return;
    try {
      var pending = readPending();
      if (pending && pending.productPathname === window.location.pathname) showProductPreview(pending);
    } catch (error) {}
    window.setTimeout(function () {
      resumePending().catch(function () {});
    }, AUTO_RESUME_DELAY_MS);
  }

  function recordAIEvent(eventType, detail) {
    detail = detail || {};
    var metadata = detail.metadata || {};
    if (!metadata.designId) return;
    var stored;
    try {
      stored = JSON.parse(sessionStorage.getItem('dyefor-ai-session-v1') || 'null');
    } catch (error) {
      return;
    }
    if (!stored || !stored.token || Number(stored.expiresAt) <= Math.floor(Date.now() / 1000)) return;
    var handle = window.location.pathname.match(/^\/products\/([^/?#]+)/);
    fetch(API_BASE + '/v1/event', {
      method: 'POST',
      headers: {
        authorization: 'Bearer ' + stored.token,
        'content-type': 'application/json'
      },
      body: JSON.stringify({
        eventType: eventType,
        designId: metadata.designId,
        requestId: metadata.requestId,
        productHandle: handle ? handle[1] : '',
        status: eventType === 'handoff_failed' ? 'failed' : 'succeeded',
        errorCode: detail.code || ''
      }),
      cache: 'no-store',
      keepalive: true
    }).catch(function () {});
  }

  document.addEventListener('dyefor:ai-image-imported', function (event) {
    recordAIEvent('customiser_imported', event.detail);
  });
  document.addEventListener('dyefor:ai-image-handoff-consumed', function (event) {
    recordAIEvent('handoff_succeeded', event.detail);
  });
  document.addEventListener('dyefor:ai-image-error', function (event) {
    recordAIEvent('handoff_failed', event.detail);
  });

  var CUSTOMIZER_SHELL_CLASS = 'dyefor-customizer-open';
  var CUSTOMIZER_ROOT_SELECTOR = '.varDlgCust, .cust-form-v3';
  var customizerLaunchPendingUntil = 0;
  var customizerSyncFrame = null;
  var customizerLaunchTimer = null;
  var watchedCustomizerRoots = new WeakSet();

  function isCustomizerRootVisible(root) {
    if (!root || !root.isConnected || root.hidden || root.getAttribute('aria-hidden') === 'true') return false;

    var style = window.getComputedStyle(root);
    return style.display !== 'none'
      && style.visibility !== 'hidden'
      && style.opacity !== '0'
      && root.getClientRects().length > 0;
  }

  function isCustomizerOpen() {
    return Array.prototype.some.call(
      document.querySelectorAll(CUSTOMIZER_ROOT_SELECTOR),
      isCustomizerRootVisible
    );
  }

  function syncCustomizerShell() {
    customizerSyncFrame = null;
    if (!document.body) return;

    var launchPending = customizerLaunchPendingUntil > Date.now();
    document.body.classList.toggle(CUSTOMIZER_SHELL_CLASS, launchPending || isCustomizerOpen());
  }

  function scheduleCustomizerShellSync() {
    if (customizerSyncFrame !== null) return;
    customizerSyncFrame = window.requestAnimationFrame(syncCustomizerShell);
  }

  var customizerStateObserver = new MutationObserver(scheduleCustomizerShellSync);

  function watchCustomizerRoots(root) {
    if (!root) return;

    var roots = [];
    if (root.nodeType === 1 && root.matches(CUSTOMIZER_ROOT_SELECTOR)) roots.push(root);
    if (root.querySelectorAll) {
      roots = roots.concat(Array.prototype.slice.call(root.querySelectorAll(CUSTOMIZER_ROOT_SELECTOR)));
    }

    roots.forEach(function (customizerRoot) {
      if (watchedCustomizerRoots.has(customizerRoot)) return;
      watchedCustomizerRoots.add(customizerRoot);
      customizerStateObserver.observe(customizerRoot, {
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'aria-hidden']
      });
    });
  }

  function beginCustomizerShell() {
    customizerLaunchPendingUntil = Date.now() + 4000;
    document.body.classList.add(CUSTOMIZER_SHELL_CLASS);
    window.clearTimeout(customizerLaunchTimer);
    customizerLaunchTimer = window.setTimeout(function () {
      customizerLaunchPendingUntil = 0;
      scheduleCustomizerShellSync();
    }, 4100);
  }

  function endCustomizerLaunchPending() {
    customizerLaunchPendingUntil = 0;
    window.clearTimeout(customizerLaunchTimer);
    window.setTimeout(scheduleCustomizerShellSync, 50);
  }

  function installCustomizerShell() {
    if (!document.body || document.getElementById('dyefor-customizer-shell-styles')) return;

    var style = document.createElement('style');
    style.id = 'dyefor-customizer-shell-styles';
    style.textContent = [
      'body.' + CUSTOMIZER_SHELL_CLASS + ' #shopify-section-announcement-bar,',
      'body.' + CUSTOMIZER_SHELL_CLASS + ' #shopify-section-header,',
      'body.' + CUSTOMIZER_SHELL_CLASS + ' #shopify-section-footer,',
      'body.' + CUSTOMIZER_SHELL_CLASS + ' #ShopifyChat,',
      'body.' + CUSTOMIZER_SHELL_CLASS + ' [data-shopify-chat],',
      'body.' + CUSTOMIZER_SHELL_CLASS + ' iframe[title*="chat" i],',
      'body.' + CUSTOMIZER_SHELL_CLASS + ' iframe[title*="support" i] { display: none !important; }',
      'body.' + CUSTOMIZER_SHELL_CLASS + ' #MainContent { margin-top: 0 !important; padding-top: 0 !important; }'
    ].join('\n');
    document.head.appendChild(style);

    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      if (target.closest('.btnStartCust')) {
        if (document.documentElement.classList.contains('dyefor-ai-direct-product')) return;
        beginCustomizerShell();
        return;
      }

      if (target.closest(
        '.varDlgCust [onclick*="CancelCustomizer"],'
        + '.cust-form-v3 [onclick*="CancelCustomizer"],'
        + '.varDlgCust [value="Cancel"],'
        + '.cust-form-v3 [value="Cancel"]'
      )) {
        endCustomizerLaunchPending();
      }
    }, true);

    var customizerTreeObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        Array.prototype.forEach.call(mutation.addedNodes, watchCustomizerRoots);
      });
      scheduleCustomizerShellSync();
    });

    watchCustomizerRoots(document);
    customizerTreeObserver.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('pageshow', scheduleCustomizerShellSync);
    scheduleCustomizerShellSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installCustomizerShell, { once: true });
  } else {
    installCustomizerShell();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoResume, { once: true });
  } else {
    autoResume();
  }
})();
